// Emergency.tsx
import * as React from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import LocalPoliceOutlinedIcon from "@mui/icons-material/LocalPoliceOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import {
  fetchCompanyEmergencyContact,
  fetchEmergencyContacts,
  fetchEmployeeScoreIdentityByAuthUserId,
  getCurrentUser,
} from "../api/queries";
import { useI18n } from "../i18n/I18nProvider";
import { brand } from "../theme";
import type { CompanyEmergencyContact } from "../types/company";
import type { EmergencyContact } from "../types/emergency";


/* ---------------- helpers ---------------- */

function normalizeTel(phone: string) {
  return phone.replace(/[^\d+]/g, "");
}

function emailHref(email: string) {
  return `mailto:${email}`;
}

function phoneHref(phone: string) {
  return `tel:${normalizeTel(phone)}`;
}

/**
 * The contacts table is shared with placeholder and customer-side rows, so the
 * page only ever surfaces people who actually answer for takeKONTROL.
 */
export function isTakeKontrolContact(contact: EmergencyContact) {
  const email = (contact.email ?? "").trim().toLowerCase();
  if (email.endsWith("@takekontrol.de")) return true;

  const positions = `${contact.position_en ?? ""} ${contact.position_de ?? ""}`.toLowerCase();
  return positions.includes("takekontrol");
}

function isCompleteCompanyContact(contact: CompanyEmergencyContact | null) {
  return Boolean(contact?.name?.trim() && contact?.email?.trim());
}

/* ---------------- UI atoms ---------------- */

function SectionIconBox(props: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: 2,
        bgcolor: brand.page,
        border: `1px solid ${brand.border}`,
        display: "grid",
        placeItems: "center",
        color: brand.textPrimary,
        flexShrink: 0,
      }}
    >
      {props.children}
    </Box>
  );
}

function SectionHeading(props: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
      <SectionIconBox>{props.icon}</SectionIconBox>
      <Box sx={{ minWidth: 0 }}>
        <Typography component="h2" sx={{ fontWeight: 900, color: brand.textPrimary }}>
          {props.title}
        </Typography>
        <Typography variant="body2" sx={{ color: brand.textMuted }}>
          {props.subtitle}
        </Typography>
      </Box>
    </Stack>
  );
}

/**
 * A public emergency number, sized so it can be dialled at a glance under
 * stress rather than read like a table row.
 */
function EmergencyNumberCard(props: {
  icon: React.ReactNode;
  /** Shown to the reader, e.g. "116 123". */
  number: string;
  /** Dialled value, e.g. "116123". */
  dial: string;
  name: string;
  useWhen: string;
  callLabel: string;
  accent?: boolean;
}) {
  const { icon, number, dial, name, useWhen, callLabel, accent = false } = props;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${accent ? brand.accent : brand.border}`,
        bgcolor: brand.surface,
        p: { xs: 1.5, md: 1.75 },
        minWidth: 0,
        // No fixed height: the grid stretches the card, and the description
        // flexes so every call button still lines up along the bottom.
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            bgcolor: accent ? brand.accent : brand.page,
            border: `1px solid ${accent ? brand.accent : brand.border}`,
            color: accent ? "#FFFFFF" : brand.textPrimary,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: 24, sm: 26, lg: 24, xl: 28 },
              lineHeight: 1.05,
              color: accent ? brand.accentStrong : brand.textPrimary,
            }}
          >
            {number}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: brand.textSecondary, fontWeight: 800, lineHeight: 1.3 }}
          >
            {name}
          </Typography>
        </Box>
      </Stack>

      <Typography
        variant="body2"
        sx={{ color: brand.textMuted, lineHeight: 1.5, flex: 1, minWidth: 0 }}
      >
        {useWhen}
      </Typography>

      <Button
        component="a"
        href={phoneHref(dial)}
        variant={accent ? "contained" : "outlined"}
        startIcon={<LocalPhoneOutlinedIcon />}
        sx={{
          textTransform: "none",
          fontWeight: 900,
          borderRadius: 2.5,
          whiteSpace: "nowrap",
          ...(accent
            ? {
                bgcolor: brand.accentStrong,
                boxShadow: "none",
                "&:hover": { bgcolor: brand.accentStrong, boxShadow: "none" },
              }
            : {
                borderColor: brand.borderStrong,
                color: brand.textPrimary,
                "&:hover": { borderColor: brand.textFaint, bgcolor: brand.surfaceMuted },
              }),
        }}
      >
        {`${callLabel} ${number}`}
      </Button>
    </Paper>
  );
}

function FieldRow(props: {
  icon: React.ReactNode;
  label: string;
  value: string;
  primaryAction?: { label: string; href: string };
  onCopy?: () => void;
}) {
  const { icon, label, value, primaryAction, onCopy } = props;

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: `1px solid ${brand.border}`,
        bgcolor: brand.surface,
        px: 1.5,
        py: 1.25,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={{ xs: 1, sm: 2 }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, maxWidth: "100%" }}>
          {icon}
          <Typography sx={{ fontWeight: 900, color: brand.textPrimary, whiteSpace: "nowrap" }}>
            {label}:
          </Typography>
          <Typography sx={{ color: brand.textPrimary, fontWeight: 700, overflowWrap: "anywhere" }}>
            {value}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          {primaryAction && (
            <Button
              component="a"
              href={primaryAction.href}
              size="small"
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 900,
                bgcolor: brand.ink,
                boxShadow: "none",
                "&:hover": { bgcolor: brand.inkDeep, boxShadow: "none" },
              }}
            >
              {primaryAction.label}
            </Button>
          )}

          {onCopy && (
            <IconButton onClick={onCopy} aria-label={`Copy ${label}`}>
              <ContentCopyOutlinedIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}

/* ---------------- main component ---------------- */

export default function Emergency() {
  const { lang } = useI18n();
  const L = React.useCallback((en: string, de: string) => (lang === "de" ? de : en), [lang]);

  const [contacts, setContacts] = React.useState<EmergencyContact[]>([]);
  const [companyContact, setCompanyContact] = React.useState<CompanyEmergencyContact | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [snack, setSnack] = React.useState<{ open: boolean; msg: string }>({
    open: false,
    msg: "",
  });

  React.useEffect(() => {
    const fetchContacts = async () => {
      const { data, error } = await fetchEmergencyContacts();

      if (error) {
        setError(error.message);
      } else {
        setContacts((data ?? []).filter(isTakeKontrolContact));
      }

      const { data: userData, error: userError } = await getCurrentUser();
      if (userError) {
        setError(userError.message);
        setLoading(false);
        return;
      }

      if (userData?.user?.id) {
        const { data: identity, error: identityError } =
          await fetchEmployeeScoreIdentityByAuthUserId(userData.user.id);

        if (identityError) {
          setError(identityError.message);
          setLoading(false);
          return;
        }

        if (identity?.company_id) {
          const { data: companyEmergencyContact, error: contactError } =
            await fetchCompanyEmergencyContact(identity.company_id);

          if (contactError) {
            setError(contactError.message);
          } else {
            setCompanyContact(isCompleteCompanyContact(companyEmergencyContact) ? companyEmergencyContact : null);
          }
        }
      }

      setLoading(false);
    };

    fetchContacts();
  }, []);

  const copy = async (value: string, labelEn: string, labelDe: string) => {
    const label = L(labelEn, labelDe);
    try {
      await navigator.clipboard.writeText(value);
      setSnack({ open: true, msg: L(`${label} copied`, `${label} kopiert`) });
    } catch {
      setSnack({ open: true, msg: L(`Could not copy ${label}`, `${label} konnte nicht kopiert werden`) });
    }
  };

  const emergencyNumbers = [
    {
      key: "112",
      icon: <LocalHospitalOutlinedIcon fontSize="small" />,
      number: "112",
      dial: "112",
      name: L("Fire, rescue and ambulance", "Feuerwehr, Rettung und Notarzt"),
      useWhen: L(
        "Life-threatening situations: fire, serious injury, unconsciousness, chest pain, accidents. Free from any phone, works across the EU.",
        "Lebensbedrohliche Lagen: Feuer, schwere Verletzung, Bewusstlosigkeit, Brustschmerz, Unfälle. Kostenlos von jedem Telefon, EU-weit erreichbar."
      ),
      accent: true,
    },
    {
      key: "110",
      icon: <LocalPoliceOutlinedIcon fontSize="small" />,
      number: "110",
      dial: "110",
      name: L("Police", "Polizei"),
      useWhen: L(
        "Crime in progress, violence or threats, suspicious objects, and anything that needs the police on scene.",
        "Laufende Straftaten, Gewalt oder Bedrohung, verdächtige Gegenstände und alles, wofür die Polizei vor Ort sein muss."
      ),
    },
    {
      key: "116117",
      icon: <MedicalServicesOutlinedIcon fontSize="small" />,
      number: "116117",
      dial: "116117",
      name: L("Medical on-call service", "Ärztlicher Bereitschaftsdienst"),
      useWhen: L(
        "Medical help that cannot wait for the practice to reopen but is not life-threatening — nights, weekends and holidays.",
        "Ärztliche Hilfe, die nicht bis zur Praxisöffnung warten kann, aber nicht lebensbedrohlich ist — nachts, am Wochenende und an Feiertagen."
      ),
    },
    {
      key: "116123",
      icon: <SupportAgentOutlinedIcon fontSize="small" />,
      number: "116 123",
      dial: "116123",
      name: L("Emotional support helpline", "TelefonSeelsorge"),
      useWhen: L(
        "Free, confidential and anonymous support in crisis, distress or after a frightening event. Available around the clock.",
        "Kostenlos, vertraulich und anonym bei Krisen, Belastung oder nach einem beängstigenden Ereignis. Rund um die Uhr erreichbar."
      ),
    },
  ];

  const callSteps = [
    L("Where is it happening? Give the address and how to find you.", "Wo ist es passiert? Nenne die Adresse und wie man dich findet."),
    L("What happened? Describe the situation in one sentence.", "Was ist passiert? Beschreibe die Lage in einem Satz."),
    L("How many people are affected?", "Wie viele Personen sind betroffen?"),
    L("What kind of injuries or danger is involved?", "Welche Verletzungen oder Gefahren liegen vor?"),
    L("Wait for questions — never hang up first.", "Warte auf Rückfragen — lege nie zuerst auf."),
  ];

  /* ---------------- render ---------------- */

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: brand.page, p: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: brand.textPrimary, mb: 0.5 }}>
          {L("Emergency Contacts", "Notfallkontakte")}
        </Typography>

        <Typography variant="body2" sx={{ color: brand.textMuted, mb: { xs: 2, md: 3 } }}>
          {L(
            "Public emergency numbers first, then how to make the call count, then how to reach takeKONTROL.",
            "Zuerst die öffentlichen Notrufnummern, dann wie der Anruf gelingt, dann wie du takeKONTROL erreichst."
          )}
        </Typography>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${brand.border}`,
            bgcolor: brand.surface,
            p: { xs: 2, md: 2.5 },
            mb: 2,
          }}
        >
          <SectionHeading
            icon={<ShieldOutlinedIcon fontSize="small" />}
            title={L("In immediate danger", "Bei akuter Gefahr")}
            subtitle={L(
              "Call these first. They take priority over anything in this app.",
              "Rufe zuerst hier an. Das hat Vorrang vor allem in dieser App."
            )}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
              },
              alignItems: "stretch",
              gap: { xs: 1.25, sm: 1.5, md: 2 },
            }}
          >
            {emergencyNumbers.map((entry) => (
              <EmergencyNumberCard
                key={entry.key}
                icon={entry.icon}
                number={entry.number}
                dial={entry.dial}
                name={entry.name}
                useWhen={entry.useWhen}
                callLabel={L("Call", "Anrufen")}
                accent={entry.accent}
              />
            ))}
          </Box>
        </Paper>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
            mb: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${brand.border}`,
              bgcolor: brand.surface,
              p: { xs: 2, md: 2.5 },
            }}
          >
            <SectionHeading
              icon={<ChecklistOutlinedIcon fontSize="small" />}
              title={L("What to say when you call", "Was du beim Anruf sagst")}
              subtitle={L(
                "Dispatchers need these five answers.",
                "Die Leitstelle braucht diese fünf Antworten."
              )}
            />

            <Stack spacing={1}>
              {callSteps.map((step, index) => (
                <Stack key={step} direction="row" spacing={1.25} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      bgcolor: brand.ink,
                      color: "#FFFFFF",
                      fontWeight: 900,
                      fontSize: 12,
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                      mt: 0.15,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography variant="body2" sx={{ color: brand.textSecondary, lineHeight: 1.55 }}>
                    {step}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${brand.border}`,
              bgcolor: brand.surface,
              p: { xs: 2, md: 2.5 },
            }}
          >
            <SectionHeading
              icon={<CampaignOutlinedIcon fontSize="small" />}
              title={L("Official warnings", "Offizielle Warnungen")}
              subtitle={L(
                "Where verified crisis information comes from.",
                "Woher geprüfte Krisen-Informationen kommen."
              )}
            />

            <Stack spacing={1.25}>
              <Box
                sx={{
                  borderRadius: 2.5,
                  border: `1px solid ${brand.border}`,
                  bgcolor: brand.surfaceMuted,
                  p: 1.5,
                }}
              >
                <Typography sx={{ fontWeight: 900, color: brand.textPrimary }}>
                  {L("NINA warning app", "Warn-App NINA")}
                </Typography>
                <Typography variant="body2" sx={{ color: brand.textMuted, mt: 0.35, lineHeight: 1.55 }}>
                  {L(
                    "Official alerts from the Federal Office of Civil Protection (BBK) for your location — install it before you need it.",
                    "Offizielle Warnungen des Bundesamts für Bevölkerungsschutz (BBK) für deinen Standort — installiere sie, bevor du sie brauchst."
                  )}
                </Typography>
              </Box>

              <Box
                sx={{
                  borderRadius: 2.5,
                  border: `1px solid ${brand.border}`,
                  bgcolor: brand.surfaceMuted,
                  p: 1.5,
                }}
              >
                <Typography sx={{ fontWeight: 900, color: brand.textPrimary }}>
                  {L("Battery radio", "Batterieradio")}
                </Typography>
                <Typography variant="body2" sx={{ color: brand.textMuted, mt: 0.35, lineHeight: 1.55 }}>
                  {L(
                    "If mobile networks and power are down, public broadcasters remain the fallback channel for official instructions.",
                    "Wenn Mobilfunk und Strom ausfallen, bleibt der öffentlich-rechtliche Rundfunk der Rückfallkanal für offizielle Anweisungen."
                  )}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Box>

        {companyContact && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${brand.border}`,
              bgcolor: brand.surface,
              p: { xs: 2, md: 2.5 },
              mb: 2,
            }}
          >
            <SectionHeading
              icon={<ApartmentOutlinedIcon fontSize="small" />}
              title={L("Company contact", "Unternehmenskontakt")}
              subtitle={L(
                "Your employer's emergency contact for company-specific questions.",
                "Der Notfallkontakt deines Arbeitgebers für unternehmensbezogene Fragen."
              )}
            />

            <Stack spacing={1.25}>
              <Typography sx={{ fontWeight: 900, color: brand.textPrimary }}>
                {companyContact.name}
              </Typography>

              <FieldRow
                icon={<EmailOutlinedIcon fontSize="small" />}
                label={L("Email", "E-Mail")}
                value={companyContact.email}
                primaryAction={{ label: L("Email", "E-Mail"), href: emailHref(companyContact.email) }}
                onCopy={() => copy(companyContact.email, "Email", "E-Mail")}
              />

              {companyContact.phone_number && (
                <FieldRow
                  icon={<LocalPhoneOutlinedIcon fontSize="small" />}
                  label={L("Phone", "Telefon")}
                  value={companyContact.phone_number}
                  primaryAction={{
                    label: L("Call", "Anrufen"),
                    href: phoneHref(companyContact.phone_number),
                  }}
                  onCopy={() => copy(companyContact.phone_number!, "Phone", "Telefon")}
                />
              )}
            </Stack>
          </Paper>
        )}

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${brand.border}`,
            bgcolor: brand.surface,
            p: { xs: 2, md: 2.5 },
          }}
        >
          <SectionHeading
            icon={<ApartmentOutlinedIcon fontSize="small" />}
            title={L("takeKONTROL contacts", "takeKONTROL-Kontakte")}
            subtitle={L(
              "For questions about the platform and your preparedness plan — not for emergencies.",
              "Für Fragen zur Plattform und deinem Vorsorgeplan — nicht für Notfälle."
            )}
          />

          {loading && <CircularProgress />}
          {error && <Alert severity="error">{error}</Alert>}

          {!loading && !error && contacts.length === 0 && (
            <Typography variant="body2" sx={{ color: brand.textMuted }}>
              {L(
                "No takeKONTROL contact is listed right now. Use the question form in your profile instead.",
                "Aktuell ist kein takeKONTROL-Kontakt hinterlegt. Nutze stattdessen das Frageformular in deinem Profil."
              )}
            </Typography>
          )}

          <Stack spacing={2}>
            {contacts.map((c) => {
              const posEn = c.position_en ?? "";
              const posDe = c.position_de ?? "";
              const position = lang === "de" ? (posDe || posEn) : posEn;

              return (
                <Box key={c.id}>
                  <Stack spacing={1.25}>
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: brand.textPrimary }}>
                        {c.first_name} {c.last_name}
                      </Typography>

                      {!!position && (
                        <Typography variant="body2" sx={{ color: brand.textMuted }}>
                          {position}
                        </Typography>
                      )}
                    </Box>

                    {c.email && (
                      <FieldRow
                        icon={<EmailOutlinedIcon fontSize="small" />}
                        label={L("Email", "E-Mail")}
                        value={c.email}
                        primaryAction={{ label: L("Email", "E-Mail"), href: emailHref(c.email) }}
                        onCopy={() => copy(c.email!, "Email", "E-Mail")}
                      />
                    )}

                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Paper>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={1500} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity="success">{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
